import type { SVGAttributes } from 'react';
import type { To } from 'react-router';
import { PortIconSVG } from 'atto-svgs';
import React from 'react';

import type { IconName } from '../../assets/Icon/Icon';
import type { PortSpeed, PortStatus, PortVariant } from './utils';
import { Icon } from '../../assets/Icon/Icon';
import { FocusRing } from '../../common/focus_rings';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { Text } from '../../text/Text';
import { getPortIcon, getPortSpeedLabel, getPortVariant } from './utils';

export { type PortSpeed, type PortStatus, type PortVariant };

const PortSVGs = {
  ethernet: PortIconSVG.Ethernet,
  sfp: PortIconSVG.SFP,
};

const portDisabledOpacity = 0.85;

const portSVGSizing = {
  simple: {
    width: '$20',
    height: '$18',
  },
  icon: {
    width: '$12',
    height: '$11',
  },
  detailed: {
    width: '$32',
    height: '$29',
  },
};

const PortSVG = styled('svg', {
  position: 'absolute',
  zIndex: 0,
  top: 0,
  left: 0,
  display: 'flex',
  overflow: 'visible',
  stroke: '$$portStrokeColor',
  paintOrder: 'stroke',
  color: '$$portBgColor',

  variants: {
    orientation: {
      up: {},
      down: {
        transform: 'rotate(180deg)',
      },
    },
    mode: {
      detailed: {
        width: portSVGSizing.detailed.width,
        height: portSVGSizing.detailed.height,
        strokeWidth: '2px',
      },
      icon: {
        widthAll: portSVGSizing.icon.width,
        heightAll: portSVGSizing.icon.height,
        strokeWidth: '4px',
      },
      simple: {
        width: portSVGSizing.simple.width,
        height: portSVGSizing.simple.height,
        strokeWidth: '3px',
      },
    },
  },
});
PortSVG.displayName = 'PortSVG';

const PortRepresentation = styled('div', {
  position: 'absolute',
  zIndex: 0,
  top: 0,
  left: 0,
  display: 'flex',
});
PortRepresentation.displayName = 'PortRepresentation';

const PortIcon = styled(Icon, {
  width: '$10',
  height: '$10',
  color: '$$portContentsColor',
  margin: '0 auto',

  variants: {
    disabled: {
      true: {
        opacity: portDisabledOpacity,
      },
      false: {},
    },
  },
});
PortIcon.displayName = 'PortIcon';

const PortCopy = styled(Text, {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  fontFamily: '$mono',
  fontSize: '$10',
  lineHeight: '$12',

  variants: {
    mode: {
      detailed: {
        heightAll: '$12',
      },
      icon: {},
      simple: {
        widthAll: portSVGSizing.simple.width,
        heightAll: portSVGSizing.simple.height,
      },
    },
    orientation: {
      up: {},
      down: {},
    },
  },

  compoundVariants: [
    {
      mode: 'simple',
      orientation: 'down',
      css: {
        paddingBottom: '$2',
      },
    },
    {
      mode: 'simple',
      orientation: 'up',
      css: {
        paddingTop: '$2',
      },
    },
  ],
});
PortCopy.displayName = 'PortCopy';

const PortPropertiesStart = styled('div', {
  display: 'flex',
});
PortPropertiesStart.displayName = 'PortPropertiesStart';

const PortPropertiesEnd = styled('div', {
  display: 'flex',
});
PortPropertiesEnd.displayName = 'PortPropertiesEnd';

const PortProperties = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  heightAll: '$10',
  padding: '0 $2',
});
PortProperties.displayName = 'PortProperties';

const PortContents = styled('div', {
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  color: '$$portContentsColor',

  variants: {
    orientation: {
      up: {
        flexDirection: 'column',
        justifyContent: 'end',
      },
      down: {
        flexDirection: 'column-reverse',
        justifyContent: 'start',
      },
    },
    mode: {
      detailed: {},
      icon: {},
      simple: {},
    },
  },
  compoundVariants: [
    {
      mode: 'detailed',
      orientation: 'up',
      css: {
        paddingBottom: '$2',
      },
    },
    {
      mode: 'detailed',
      orientation: 'down',
      css: {
        paddingTop: '$2',
      },
    },
  ],
});
PortContents.displayName = 'PortContents';

const PortStamp = styled('span', {
  position: 'absolute',
  left: '50%',
  display: 'flex',
  zIndex: 2,
  alignItems: 'center',
  justifyContent: 'center',
  height: '$10',
  padding: '0 $2',
  fontFamily: '$mono',
  fontSize: '$9',
  borderRadius: '$4',
  backgroundColor: '$$portStrokeColor',
  color: '$$portStampColor',

  variants: {
    disabled: {
      true: {
        opacity: portDisabledOpacity,
      },
      false: {},
    },
    orientation: {
      up: {
        top: 0,
        transform: 'translate(-50%, -50%)',
      },
      down: {
        bottom: 0,
        transform: 'translate(-50%, 50%)',
      },
    },
  },
});
PortStamp.displayName = 'PortStamp';

const PortContainer = styled('div', FocusRing, {
  position: 'relative',
  display: 'flex',
  alignItems: 'stretch',
  justifyContent: 'stretch',

  variants: {
    mode: {
      detailed: {
        widthAll: portSVGSizing.detailed.width,
        heightAll: portSVGSizing.detailed.height,
        borderRadius: '$6',
      },
      icon: {
        widthAll: portSVGSizing.icon.width,
        heightAll: portSVGSizing.icon.height,
        borderRadius: '$4',
      },
      simple: {
        widthAll: portSVGSizing.simple.width,
        heightAll: portSVGSizing.simple.height,
        borderRadius: '$4',
      },
    },
    selected: {
      true: {},
      false: {},
    },
    variant: {
      alternative: {
        $$portBgColor: colors.hardwareBgAlternativeLight,
        $$portContentsColor: colors.hardwareContentAlternativeLight,
        $$portStrokeColor: colors.hardwareStrokeAlternativeLight,
        $$portStampColor: colors.hardwareStampAlternativeLight,

        [darkThemeSelector]: {
          $$portBgColor: colors.hardwareBgAlternativeDark,
          $$portContentsColor: colors.hardwareContentAlternativeDark,
          $$portStrokeColor: colors.hardwareStrokeAlternativeDark,
          $$portStampColor: colors.hardwareStampAlternativeDark,
        },
      },
      attention: {
        $$portBgColor: colors.hardwareBgAttentionLight,
        $$portContentsColor: colors.hardwareContentAttentionLight,
        $$portStrokeColor: colors.hardwareStrokeAttentionLight,
        $$portStampColor: colors.hardwareStampAttentionLight,

        [darkThemeSelector]: {
          $$portBgColor: colors.hardwareBgAttentionDark,
          $$portContentsColor: colors.hardwareContentAttentionDark,
          $$portStrokeColor: colors.hardwareStrokeAttentionDark,
          $$portStampColor: colors.hardwareStampAttentionDark,
        },
      },
      brand: {
        $$portBgColor: colors.hardwareBgBrandLight,
        $$portContentsColor: colors.hardwareContentBrandLight,
        $$portStrokeColor: colors.hardwareStrokeBrandLight,
        $$portStampColor: colors.hardwareStampBrandLight,

        [darkThemeSelector]: {
          $$portBgColor: colors.hardwareBgBrandDark,
          $$portContentsColor: colors.hardwareContentBrandDark,
          $$portStrokeColor: colors.hardwareStrokeBrandDark,
          $$portStampColor: colors.hardwareStampBrandDark,
        },
      },
      disabled: {
        $$portBgColor: colors.hardwareBgDisabledLight,
        $$portContentsColor: colors.hardwareContentDisabledLight,
        $$portStrokeColor: colors.hardwareStrokeDisabledLight,
        $$portStampColor: colors.hardwareStampDisabledLight,

        [darkThemeSelector]: {
          $$portBgColor: colors.hardwareBgDisabledDark,
          $$portContentsColor: colors.hardwareContentDisabledDark,
          $$portStrokeColor: colors.hardwareStrokeDisabledDark,
          $$portStampColor: colors.hardwareStampDisabledDark,
        },
      },
      negative: {
        $$portBgColor: colors.hardwareBgNegativeLight,
        $$portContentsColor: colors.hardwareContentNegativeLight,
        $$portStrokeColor: colors.hardwareStrokeNegativeLight,
        $$portStampColor: colors.hardwareStampNegativeLight,

        [darkThemeSelector]: {
          $$portBgColor: colors.hardwareBgNegativeDark,
          $$portContentsColor: colors.hardwareContentNegativeDark,
          $$portStrokeColor: colors.hardwareStrokeNegativeDark,
          $$portStampColor: colors.hardwareStampNegativeDark,
        },
      },
      neutral: {
        $$portBgColor: colors.hardwareBgNeutralLight,
        $$portContentsColor: colors.hardwareContentNeutralLight,
        $$portStrokeColor: colors.hardwareStrokeNeutralLight,
        $$portStampColor: colors.hardwareStampNeutralLight,

        [darkThemeSelector]: {
          $$portBgColor: colors.hardwareBgNeutralDark,
          $$portContentsColor: colors.hardwareContentNeutralDark,
          $$portStrokeColor: colors.hardwareStrokeNeutralDark,
          $$portStampColor: colors.hardwareStampNeutralDark,
        },
      },
      positive: {
        $$portBgColor: colors.hardwareBgPositiveLight,
        $$portContentsColor: colors.hardwareContentPositiveLight,
        $$portStrokeColor: colors.hardwareStrokePositiveLight,
        $$portStampColor: colors.hardwareStampPositiveLight,

        [darkThemeSelector]: {
          $$portBgColor: colors.hardwareBgPositiveDark,
          $$portContentsColor: colors.hardwareContentPositiveDark,
          $$portStrokeColor: colors.hardwareStrokePositiveDark,
          $$portStampColor: colors.hardwareStampPositiveDark,
        },
      },
    },
  },
});
PortContainer.displayName = 'PortContainer';

export type PortIconName = keyof typeof PortSVGs;

export type PortOnClick = (event: any) => void;
export type PortOrientation = 'down' | 'up';
export type PortPort = 'ethernet' | 'sfp';
export type PortMode = 'detailed' | 'icon' | 'simple';

export type PortProps = {
  /**
   * The port is blocking.
   */
  blocking?: boolean;
  /**
   * The connection of the port.
   */
  connection?: {
    selected?: boolean;
    /**
     * The hardware of the connection.
     */
    icon: IconName;
    /**
     * The label of the connection.
     */
    label: React.ReactNode;
    /**
     * The function to call when the connection is clicked.
     */
    onClick?: PortOnClick;
    /**
     * The side of the connection.
     */
    side?: 'bottom' | 'top';
    // Routing
    as?: React.ElementType;
    to?: To;
  };
  /**
   * The port is muted.
   */
  muted?: boolean;
  /**
   * The port number.
   */
  number?: number;
  /**
   * The function to call when the port is clicked.
   */
  onClick?: PortOnClick;
  /**
   * The orientation of the port, either `up` or `down`.
   */
  orientation?: PortOrientation;
  /**
   * Displays the power-over-ethernet icon, pass in a number to display PoE as drawn power.
   */
  poe?: boolean | number;
  /**
   * The port type, either `ethernet` or `sfp`.
   */
  port: PortPort;
  /**
   * The port is selected.
   */
  selected?: boolean;
  /**
   * The port max speed in Mbps.
   */
  maximumPortSpeed?: PortSpeed;
  /**
   * Current negotiated speed of the port.
   */
  negotiatedPortSpeed?: PortSpeed;
  /**
   * The port is uplink.
   */
  uplink?: boolean;
  /**
   * Whether the port is disabled, takes precedence over disabled status.
   */
  isDisabled?: boolean;
  /**
   * The port status, either `connected`, `disconnected`, `error`, or `disabled`.
   */
  status?: PortStatus;
  /**
   * The port variant.
   */
  variant?: PortVariant;
  /**
   * The mode of the port, either `detailed`, `icon`, or `simple`.
   */
  mode?: PortMode;
  /**
   * The port is an Out-of-Band port.
   */
  isOutOfBandPort?: boolean;
  /**
   * The port is a high availability port.
   */
  isHAPort?: boolean;
  /**
   * The port is a gateway port.
   */
  isGatewayPort?: boolean;
};

export function Port({
  blocking = false,
  connection,
  number,
  onClick,
  orientation = 'up',
  poe = false,
  port,
  maximumPortSpeed,
  negotiatedPortSpeed,
  uplink = false,
  selected = false,
  isDisabled,
  status = 'disconnected',
  variant,
  mode = 'detailed',
  isHAPort,
  isOutOfBandPort,
  ...remaining
}: PortProps) {
  const NamedIcon = PortSVGs[port as PortIconName] as
    | React.ComponentType<SVGAttributes<SVGSVGElement>>
    | undefined;

  const isIcon = mode === 'icon';
  const isSimplified = mode === 'simple' || isIcon;

  const portIcon = getPortIcon(uplink, blocking);
  const disabled = isDisabled || status === 'disabled';
  const portVariant = variant ?? getPortVariant(negotiatedPortSpeed, status);
  const poeIcon = typeof poe === 'number' ? 'lightning-filled' : 'lightning';
  const showSpeed = !isSimplified;

  return (
    <PortContainer
      tabIndex={onClick && 0}
      data-port={port}
      mode={mode}
      variant={portVariant}
      selected={selected}
      {...remaining}
    >
      {!isSimplified && port === 'sfp' && (
        <PortStamp disabled={disabled} orientation={orientation}>
          SFP
        </PortStamp>
      )}
      <PortContents
        onClick={onClick}
        role={onClick && 'button'}
        orientation={orientation}
        mode={mode}
      >
        {!isIcon && (
          <>
            <PortCopy mode={mode} orientation={orientation}>
              {maximumPortSpeed && showSpeed && getPortSpeedLabel(maximumPortSpeed)}
              {isSimplified && (!isOutOfBandPort ? number : '~')}
            </PortCopy>
            {!isSimplified && (
              <PortProperties>
                {disabled && !isSimplified && <PortIcon disabled={disabled} icon="cross-circle" />}
                {!isSimplified && (
                  <>
                    <PortPropertiesStart>
                      {portIcon && <PortIcon disabled={disabled} icon={portIcon} />}
                    </PortPropertiesStart>
                    <PortPropertiesEnd>
                      {poe && <PortIcon disabled={disabled} icon={poeIcon} />}
                    </PortPropertiesEnd>
                  </>
                )}
              </PortProperties>
            )}
          </>
        )}
      </PortContents>
      <PortRepresentation>
        <PortSVG as={NamedIcon} orientation={orientation} mode={mode} />
      </PortRepresentation>
    </PortContainer>
  );
}
